import {
  FILTER_REGISTRY,
  MovementMethodSchema,
  PersonNowSchema,
  writeFilterParams,
  type FilterKey,
  type MovementMethodFilter,
  type PersonNow,
} from '@attendance/shared';
import { readListUrl, withAliases, type ScreenAliases } from '../../filters/useListUrlState.js';
import { isYmd } from '../attendance/attendanceCalendarModel.js';

/* ===========================================================================
 * THE PEOPLE LIST'S STATE IS ITS ADDRESS, beside the board's own keys (`what`,
 * `how`, the site filter) — so a link pasted into chat opens the same list.
 *
 * ONE ADDRESS FOR THE MAP AND THE TABLE (owner, 2026-10-24: "unify filters,
 * especially for employees as map or as table — there are two different
 * panels?"). Both views read and write it through `usePeopleFilters`, and draw
 * one panel over it (`PeopleFilterPanel`).
 *
 * EVERY KEY IS THE REGISTRY'S (merge M7; packages/shared/src/filters/
 * registry.ts) and moves through `useListUrlState`: `q` (the search), `day`,
 * `at` ("Punched at" — the site of the last punch that day, never "Site"),
 * `now`, `method`, `flagged`, `group`, `dept` and `position` (by designation
 * id, owner D2). So the words, the values and the server params are the ones
 * every other list uses, and a chip names each.
 *
 * OLDER LINKS STILL OPEN THE SAME LIST, and the next write spells them the
 * registry's way: `search` → `q`, `ws` → `at`, `flagged=true` → `flagged=1`
 * (registry legacy keys and values); `pos` → `position` and the On-site
 * tile's `onSite=true` → `now=onSite` (this screen's own aliases, below).
 *
 * `notPunched` and `unaccounted` are not filters: they are the populations a
 * tile or an alert counted on the live board (OperationsPage
 * `EXCEPTION_WRITE`), read here as they have always been written, today only.
 *
 * Defaults are left out: bare is today. A tampered value reads as absent.
 * ======================================================================== */

export type PeopleException = 'notPunched' | 'unaccounted';

export interface PeopleFilters {
  day: string;
  /** The search as it is in the address — what the lists are narrowed by. */
  search: string;
  /** "Punched at": the site of the last punch that day (registry `at`); '' is anywhere. */
  worksiteId: string;
  now: PersonNow | '';
  method: MovementMethodFilter | '';
  flagged: boolean;
  /** The workforce group — '' | workers | staff | remote (the registry's `group`). */
  group: string;
  /** One department id; '' is all. */
  departmentId: string;
  /** One position by its designation id (owner D2); '' is all. An older link's title still reads. */
  position: string;
  /** A tile's population, from the live board — today only. */
  exception: PeopleException | null;
}

/** The popover's filters, in its order — what the badge counts and the chips name. */
export const PEOPLE_FILTER_KEYS = ['at', 'now', 'method', 'group', 'dept', 'position', 'flagged'] as const satisfies readonly FilterKey[];
export type PeopleFilterKey = (typeof PEOPLE_FILTER_KEYS)[number];
/** Every registry key the People list keeps in its address. */
export const PEOPLE_KEYS = ['q', 'day', ...PEOPLE_FILTER_KEYS] as const;
export type PeopleKey = (typeof PEOPLE_KEYS)[number];

/**
 * What `/attendance/people-day` is asked, under the registry's shared params
 * (`filterServerParams`): the search as `q`, "Punched at" as `worksiteId`,
 * `now`, `method`, `flagged`, `group`, `departmentId` and `designationId`.
 * Every one is answered by the server over the whole day (M7), for the list
 * and its file alike (merge E3).
 */
export const PEOPLE_SERVER_KEYS = ['q', 'at', 'now', 'method', 'flagged', 'group', 'dept', 'position'] as const satisfies readonly FilterKey[];

/** This screen's own older keys — read when the canonical key is absent, dropped on the next write. */
export const PEOPLE_ALIASES: ScreenAliases<PeopleKey> = { position: ['pos'], now: ['onSite'] };

const EXCEPTIONS: readonly PeopleException[] = ['notPunched', 'unaccounted'];

/**
 * Every URL key the People list reads, any spelling — what a tile clears
 * before it writes its one population, so a tile reading 12 opens 12.
 */
export const PEOPLE_URL_KEYS: readonly string[] = [
  ...PEOPLE_KEYS.flatMap((k) => [FILTER_REGISTRY[k].urlKey, ...(FILTER_REGISTRY[k].legacyUrlKeys ?? [])]),
  ...Object.values(PEOPLE_ALIASES).flat(),
];

/** Nothing narrowed, on `today` — what a tile writes its one population over. */
export function noPeopleFilters(today: string): PeopleFilters {
  return { day: today, search: '', worksiteId: '', now: '', method: '', flagged: false, group: '', departmentId: '', position: '', exception: null };
}

function oneOf<T extends string>(schema: { safeParse: (v: unknown) => { success: boolean } }, v: string | undefined): T | '' {
  return v !== undefined && schema.safeParse(v).success ? (v as T) : '';
}

/** The list's filters from the registry values `useListUrlState` read, plus the tile's population. */
export function peopleFiltersFrom(
  values: Partial<Record<PeopleKey, string>>,
  params: URLSearchParams,
  today: string
): PeopleFilters {
  const day = values.day;
  const onDay = day && isYmd(day) && day <= today ? day : today;
  const isToday = onDay === today;
  return {
    day: onDay,
    search: values.q ?? '',
    worksiteId: values.at ?? '',
    now: oneOf<PersonNow>(PersonNowSchema, values.now),
    method: oneOf<MovementMethodFilter>(MovementMethodSchema, values.method),
    flagged: values.flagged === '1',
    group: values.group ?? '',
    departmentId: values.dept ?? '',
    position: values.position ?? '',
    exception: isToday ? (EXCEPTIONS.find((e) => params.get(e) === 'true') ?? null) : null,
  };
}

/** The registry values for a patch of the list's filters — `undefined` clears a key. */
export function peopleValues(f: Partial<PeopleFilters>, today: string): Partial<Record<PeopleKey, string | undefined>> {
  const out: Partial<Record<PeopleKey, string | undefined>> = {};
  const put = (k: PeopleKey, v: string | undefined): void => {
    out[k] = v || undefined;
  };
  if ('day' in f) put('day', f.day === today ? undefined : f.day);
  if ('search' in f) put('q', f.search);
  if ('worksiteId' in f) put('at', f.worksiteId);
  if ('now' in f) put('now', f.now);
  if ('method' in f) put('method', f.method);
  if ('flagged' in f) put('flagged', f.flagged ? '1' : undefined);
  if ('group' in f) put('group', f.group);
  if ('departmentId' in f) put('dept', f.departmentId);
  if ('position' in f) put('position', f.position);
  return out;
}

/** `today` is the COMPANY's day key (`useCompanyToday`) — time-and-dates §1. */
export function readPeopleUrl(params: URLSearchParams, today: string): PeopleFilters {
  return peopleFiltersFrom(readListUrl(params, PEOPLE_KEYS, PEOPLE_ALIASES), params, today);
}

/** `prev` with this list's keys rewritten the registry's way, and the board's own kept. */
export function writePeopleUrl(prev: URLSearchParams, f: PeopleFilters, today: string): URLSearchParams {
  const out = writeFilterParams(withAliases(prev, PEOPLE_ALIASES), peopleValues(f, today), PEOPLE_KEYS);
  for (const e of EXCEPTIONS) out.delete(e);
  if (f.exception) out.set(f.exception, 'true');
  return out;
}

/** What the Filters trigger's badge counts — the popover's own controls. */
export function popoverCount(f: PeopleFilters): number {
  return [f.worksiteId, f.now, f.method, f.group, f.departmentId, f.position].filter(Boolean).length + (f.flagged ? 1 : 0);
}

/** The People list, as a table, with `extra` carried. */
export function peopleHref(extra?: URLSearchParams): string {
  const p = new URLSearchParams({ what: 'people', how: 'table' });
  extra?.forEach((v, k) => p.set(k, v));
  return `/operations?${p.toString()}`;
}

/**
 * The retired Movements links — `/attendance/movements` and `list=movements` —
 * onto this list. A period becomes its LAST day (the list is one day); a
 * search and a place carry; the rest named things this list no longer asks.
 */
export function fromRetiredMovements(old: URLSearchParams): URLSearchParams {
  const out = new URLSearchParams();
  const to = old.get('to');
  if (to && isYmd(to)) out.set('day', to);
  const search = old.get('search') ?? old.get('q');
  if (search) out.set('q', search);
  const at = old.get('ws') ?? old.get('site');
  if (at) out.set('at', at);
  const method = old.get('method') ?? old.get('how');
  if (method && MovementMethodSchema.safeParse(method).success) out.set('method', method);
  if ((old.get('standing') ?? old.get('status')) === 'flagged') out.set('flagged', '1');
  return out;
}
